'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProfileMeDto } from '@diritto/common/account/dtos/profile-me.dto';
import { SsoDto } from '@diritto/common/account/dtos/sso.dto';
import { UserMeDto } from '@diritto/common/account/dtos/user-me.dto';
import { PartnerDto } from '@diritto/common/partner/dtos/partner.dto';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { AccountApi } from 'api/ApiHelper';
import { QueryKey } from 'constants/reactQuery';
import { useGetAccountMeQuery } from 'queries/account/useGetAccountMeQuery';
import { FullPageLoading } from 'ui-components/common/Loading';
import { useClient } from 'ui-hooks/useClient';
import { useRandomSeed } from 'ui-hooks/useRandomSeed';
import { checkIncludeAdult } from 'utils/checkAccessBlock';

interface AuthState {
  user?: UserMeDto;
  profile?: ProfileMeDto;
  partner?: PartnerDto;
  ssoList?: SsoDto[];
  isLoggedIn: boolean;
  includeAdult: boolean;
  isLoaded: boolean;
  signOut: () => Promise<void>;
  changeProfile: (profileId: number) => Promise<void>;
  sessionSeed: number;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider = ({ children, sessionSeed }: { sessionSeed?: number; children: ReactNode }) => {
  const queryClient = useQueryClient();
  const client = useClient();
  const { data: accountMeData, isError, isFetched } = useGetAccountMeQuery();
  const [showLoading, setShowLoading] = useState(false);
  const seed = useRandomSeed(sessionSeed);

  const handleRefetch = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: [QueryKey.User], type: 'all' });
  }, [queryClient]);

  const handleChangeProfile = useCallback(
    async (profileId: number) => {
      try {
        setShowLoading(true);
        await AccountApi.replaceToken({ profileId });
      } catch (e) {
        // ignore and just invalidates query
      } finally {
        setShowLoading(false);
        await queryClient.invalidateQueries({ refetchType: 'all' });
      }
    },
    [queryClient],
  );

  const handleSignOut = useCallback(async () => {
    try {
      await AccountApi.signOut();
    } catch (e) {
      // ignore 401
    }
    // UI 갱신을 위해 invalidateQueries 를 한다.
    await queryClient.invalidateQueries({ queryKey: [QueryKey.User], refetchType: 'all' });
    await handleRefetch();
  }, [handleRefetch, queryClient]);

  const [state, setState] = useState<AuthState>({
    user: accountMeData?.user,
    partner: accountMeData?.partner,
    profile: accountMeData?.profile,
    isLoggedIn: !!accountMeData?.user, // User 가 있는 경우 로그인으로 초기화
    includeAdult: accountMeData?.user ? checkIncludeAdult(client, accountMeData.user) : false,
    signOut: handleSignOut,
    changeProfile: handleChangeProfile,
    isLoaded: isError || isFetched,
    sessionSeed: seed,
    refetch: handleRefetch,
  });

  useEffect(() => {
    setState(prev => ({
      ...prev,
      user: accountMeData?.user,
      partner: accountMeData?.partner,
      profile: accountMeData?.profile,
      sso: accountMeData?.sso,
      ssoList: accountMeData?.ssoList,
      isLoggedIn: !!accountMeData?.user,
      includeAdult: accountMeData?.user ? checkIncludeAdult(client, accountMeData.user) : false,
      isLoaded: isError || isFetched,
      sessionSeed: seed,
      refetch: handleRefetch,
    }));
  }, [client, accountMeData, isError, isFetched, seed, handleRefetch]);

  return (
    <AuthContext.Provider value={state}>
      {showLoading && <FullPageLoading />}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthState = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useAuthState was used outside of its Provider');
  }

  return context;
};
